import { Box, Flex, Heading, Image } from '@chakra-ui/react';
import styled from '@emotion/styled';
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
  UseInfiniteQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { FilterIcon } from 'ui-components/dist/FilterIcon';
import { IClientApiResponse } from '~/clientApi';
import { sortDirections, sortFields } from '~/clientApi/agencies.model';
import { Creator } from '~/clientApi/creator.model';
import { SavedList } from '~/clientApi/savedLists.model';
import { CreatorActionModalProvider } from '~/components/CreatorActionModal/CreatorActionModal.context';
import { ListViewV2 } from '~/components/ListViewV2/ListViewV2';
import { DownloadPdf } from '~/components/SavedListShareModal';
import Spinner from '~/components/Spinner';
import { TalentRosterEmptyResults } from '~/components/TalentRosterEmptyResults';
import { FEATURE_FLAGS, MAX_CREATORS_FOR_LIST_PDF } from '~/constants';
import { MediaKit } from '~/hooks/api/media-kits/get';
import { useFetchDefaultMediaKit } from '~/hooks/api/media-kits/useFetchDefaultMediaKit';
import { useGetSharedSavedListCreators } from '~/hooks/api/useSavedLists';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { TalentFilters } from '~/screens/MyAgency/components/CreatorsFiltersDrawer';
import { FiltersDrawer } from '~/screens/MyAgency/components/FiltersDrawer';
import { ToggleView } from '~/screens/MyAgency/components/ToggleView';
import { useFilterSortFields } from '~/screens/MyAgency/hooks/useFilterSortFields';
import { SearchCreatorsApiResponse, allowedSortFields } from '~/searchApi';
import { IShareLinkMapped } from '~/shareLinkApi/shareLink.model';
import { ICreator } from '~/types';

import {
  SIXTEENTH_MANAGER_IDS,
  TESTING_MANAGER_IDS,
} from '../MessageToolModal/constants';
import { AppliedAdvancedFilters } from '../MyAgency/components/AppliedFilters';
import { ClearAllFiltersButton } from '../MyAgency/components/ClearAllFiltersButton';
import { ListView } from '../MyAgency/components/ListView';
import { SortCreatorsDropDown } from '../MyAgency/components/SortCreatorsDropdown';
import { StackCreatorSkeleton } from '../MyAgency/components/StackView/CreatorSkeleton';
import {
  IStackViewCreatorProps,
  StackViewCreator,
} from '../MyAgency/components/StackView/StackViewCreator';
import { AgencyHeaderButton } from '../MyAgency/components/Table/AgencyHeaderButton';
import { VIEW_TYPE, ViewType } from '../MyAgency/hooks/useActiveView';
import { useGroupFilters } from '../MyAgency/hooks/useGroupFilters';
import { useMakeAdvancedFiltersToString } from '../MyAgency/hooks/useMakeAdvancedFiltersToString';
import {
  useAdvancedFiltersStore,
  useCreatorsFiltersStore,
} from '../MyAgency/store';
import { SHARED_TOKEN_PREFIX } from '../SharedSavedList/SharedSavedList';

export interface ISharedSavedListContentProps {
  activeView: ViewType;
  setActiveView?: (view: ViewType) => void;
  list: SavedList;
  link?: IShareLinkMapped;
  onNoteOpen: (creator: ICreator) => void;
  onMessageOpen?: (creator: ICreator) => void;
  sharedToken?: string;
  isLegacyRoute?: boolean;
}

const ListContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  flex-wrap: wrap;
  & > * {
    width: 100%;
  }
`;

export const SharedSavedListContent: React.FC<ISharedSavedListContentProps> = ({
  activeView,
  setActiveView,
  list,
  link,
  onNoteOpen,
  onMessageOpen,
  sharedToken,
  isLegacyRoute = false,
}) => {
  const {
    sortField,
    sortDirection,
    setSortingOrder,
    toggleOrder,
    isFieldSorted,
  } = useFilterSortFields({});

  // Only show message tool for sixteenth and testing managers
  const shouldShowMessageTool = useMemo(() => {
    if (!link) return false;
    const linkOwner = link?.owners?.find(
      (owner: { ownerType: string }) => owner.ownerType === 'manager',
    );

    const isSixteenthManager = SIXTEENTH_MANAGER_IDS.includes(
      linkOwner?.ownerId ?? '',
    );
    const isTestingManager = TESTING_MANAGER_IDS.includes(
      linkOwner?.ownerId ?? '',
    );
    return isSixteenthManager || isTestingManager;
  }, [link]);

  const {
    openCreatorsFiltersDrawer,
    appliedFilters: appliedCreatorFilters,
    clearAllFilters,
  } = useCreatorsFiltersStore();

  const fetchDefaultMediaKit = useFetchDefaultMediaKit();

  const {
    setIsDrawerOpen: openFiltersDrawer,
    appliedFilters,
    clearFilters,
  } = useAdvancedFiltersStore();

  const appliedFiltersGrouped = useGroupFilters(appliedFilters);
  const advancedFiltersAsString = useMakeAdvancedFiltersToString(
    appliedFiltersGrouped,
  );

  const allFilters = [...advancedFiltersAsString, ...appliedCreatorFilters];

  const name = list?.name || list?.agencyName || '';
  const isAudienceFiltersEnabled = link
    ? !link.options?.hideAudienceFilters
    : list?.enabledControls.includes('audience-filters') || false;

  const handleAudienceClick = useCallback(() => {
    openFiltersDrawer(true);
  }, [openFiltersDrawer]);

  const handleTalentClick = useCallback(() => {
    openCreatorsFiltersDrawer();
  }, [openCreatorsFiltersDrawer]);

  const handleClearFilters = useCallback(() => {
    clearAllFilters();
    clearFilters();
  }, [clearAllFilters, clearFilters]);

  const {
    data,
    fetchNextPage,
    isInitialFetch,
    isFetchingNextPage,
    hasNextPage,
    isLoading,
    originalQuery,
  } = useGetSharedSavedListCreators({
    id: list?.id,
    sharedToken,
    filters: allFilters,
    sortField,
    sortDirection,
    pageSize: 30,
  });

  const creators = data?.creators || [];

  const totalItems = data?.totalItems;

  useEffect(() => {
    if (!list.agencyName) return;
    fetchDefaultMediaKit.mutate({
      token: sharedToken?.replace(SHARED_TOKEN_PREFIX, ''),
    });
  }, [list.agencyName, sharedToken]);

  return (
    <>
      <Box>
        <Flex
          width="100%"
          justifyContent="space-between"
          marginBottom={4}
          alignItems="center"
          flexWrap="wrap"
          flexDirection={{ base: 'column', md: 'row' }}
          gap={4}
        >
          <Flex gap={2} alignItems="center">
            {list?.agencyLogo && (
              <Image src={list.agencyLogo} height={12} alt={name} />
            )}
            <Heading
              as="h1"
              fontSize="32px"
              fontStyle="normal"
              fontWeight="medium"
              lineHeight="40px"
            >
              {name}
            </Heading>
          </Flex>
          <Flex
            justifyContent={{
              base: 'center',
              md: 'flex-end',
            }}
            alignItems="center"
            gap={2}
            flexWrap="wrap"
          >
            {allFilters.length > 0 ? (
              <ClearAllFiltersButton onClear={handleClearFilters} />
            ) : null}
            <Box
              order={{
                base: 1,
                md: -1,
              }}
            >
              <SortCreatorsDropDown
                isShared
                sortField={sortField}
                sortDirection={sortDirection}
                setSortingOrder={setSortingOrder}
              />
            </Box>
            <AgencyHeaderButton
              onClick={handleTalentClick}
              icon={FilterIcon}
              title="Talent"
              aria-label="Talent Filter"
              iconOnly={true}
              iconProps={{ boxSize: 4 }}
              track-id="foam-myagency-talent-filters"
            />
            {isAudienceFiltersEnabled && (
              <AgencyHeaderButton
                onClick={handleAudienceClick}
                icon={FilterIcon}
                title="Audience"
                aria-label="Audience Filters"
                iconOnly={false}
                iconProps={{ boxSize: 4 }}
              />
            )}
            <ToggleView
              defaultValue={activeView}
              setActiveView={setActiveView}
              radioGroupProps={{
                flex: 1,
              }}
              radioButtonProps={{ width: '100%' }}
            />
            {!isLegacyRoute && (
              <div>
                <DownloadPdf
                  fileName={
                    name
                      ? name.toLowerCase().replace(/\s+/g, '-')
                      : 'saved-list'
                  }
                  listId={list.id}
                  agencyName={list.agencyName}
                  agencyLogo={list.agencyLogo}
                  listName={list.name}
                  enabled={true}
                  fullWidth={true}
                  isButtonDisabled={
                    isLoading || totalItems > MAX_CREATORS_FOR_LIST_PDF
                  }
                  disabledTooltip={`Export unavailable: this list exceeds our ${MAX_CREATORS_FOR_LIST_PDF} talent limit.`}
                />
              </div>
            )}
          </Flex>
        </Flex>
        <AppliedAdvancedFilters
          isShared
          filters={appliedFiltersGrouped}
          enabledControls={list?.enabledControls || []}
        />
        <SharedSavedListView
          activeView={activeView}
          allFilters={allFilters}
          creators={creators}
          fetchDefaultMediaKit={fetchDefaultMediaKit}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isFieldSorted={isFieldSorted}
          isInitialFetch={isInitialFetch}
          isLoading={isLoading}
          onFetchNextPage={fetchNextPage}
          onNoteOpen={onNoteOpen}
          onMessageOpen={shouldShowMessageTool ? onMessageOpen : undefined}
          originalQuery={originalQuery}
          setSortingOrder={setSortingOrder}
          sortDirection={sortDirection}
          sortField={sortField}
          toggleOrder={toggleOrder}
          totalItems={totalItems}
        />
      </Box>
      {/* Sidebars */}
      <TalentFilters />
      <FiltersDrawer />
    </>
  );
};

function SharedSavedListView({
  activeView,
  allFilters,
  creators,
  fetchDefaultMediaKit,
  hasNextPage,
  isFetchingNextPage,
  isFieldSorted,
  isInitialFetch,
  isLoading,
  onFetchNextPage,
  onNoteOpen,
  onMessageOpen,
  originalQuery,
  setSortingOrder,
  sortDirection,
  sortField,
  toggleOrder,
  totalItems,
}: {
  activeView: ViewType;
  allFilters: string[];
  creators: ICreator[];
  fetchDefaultMediaKit: UseMutationResult<
    MediaKit,
    unknown,
    {
      creatorId?: string;
      token?: string;
    },
    unknown
  >;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFieldSorted: (field: string) => false | sortDirections;
  isInitialFetch: boolean;
  isLoading: boolean;
  onFetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<
    InfiniteQueryObserverResult<
      IClientApiResponse<Creator> | SearchCreatorsApiResponse | undefined,
      unknown
    >
  >;
  onNoteOpen: (creator: ICreator) => void;
  onMessageOpen?: (creator: ICreator) => void;
  toggleOrder: (field: allowedSortFields) => void;
  totalItems: number;
  originalQuery: UseInfiniteQueryResult<IClientApiResponse<Creator>, unknown>;
  setSortingOrder: (field: sortFields, direction: sortDirections) => void;
  sortDirection: sortDirections;
  sortField: sortFields;
}) {
  const { isEnabled: foamSharedListNewTable } = useFeature(
    FEATURE_FLAGS.foamSharedListNewTable,
  );

  const handleFetchNextPage = useCallback(() => {
    if (hasNextPage) {
      onFetchNextPage();
    }
  }, [hasNextPage, onFetchNextPage]);

  if (activeView === VIEW_TYPE.list && foamSharedListNewTable) {
    if (!isLoading && allFilters.length > 0 && creators.length === 0) {
      return <TalentRosterEmptyResults />;
    }

    return (
      <CreatorActionModalProvider>
        <ListViewV2
          infiniteQueryReturn={originalQuery}
          setSortingOrder={setSortingOrder}
          sortDirection={sortDirection}
          sortField={sortField}
          isShared
        />
      </CreatorActionModalProvider>
    );
  }

  if (isLoading) {
    return <Spinner centered />;
  }

  if (allFilters.length > 0 && creators.length === 0) {
    return <TalentRosterEmptyResults />;
  }

  if (activeView === VIEW_TYPE.stacked || activeView === VIEW_TYPE['card']) {
    return (
      <Box>
        <Virtuoso
          components={{
            // @ts-ignore
            List: ListContainer,
            ScrollSeekPlaceholder: StackCreatorSkeleton,
          }}
          data={creators}
          increaseViewportBy={300}
          useWindowScroll
          endReached={handleFetchNextPage}
          totalCount={totalItems}
          itemContent={(_index, creator) => {
            const stackViewCreatorOptionalProps: Partial<IStackViewCreatorProps> =
              {};

            stackViewCreatorOptionalProps.note = creator?.notes || undefined;
            stackViewCreatorOptionalProps.onNoteOpen = () =>
              onNoteOpen(creator);
            !!onMessageOpen &&
              (stackViewCreatorOptionalProps.onMessageOpen = () =>
                onMessageOpen(creator));

            return (
              <StackViewCreator
                creator={creator}
                isCheckEnabled={false}
                isShared
                key={creator.id}
                withContent={activeView === VIEW_TYPE.stacked}
                {...stackViewCreatorOptionalProps}
                showMediaKitBtn={!!fetchDefaultMediaKit.data}
              />
            );
          }}
        />
      </Box>
    );
  }

  return (
    // @ts-ignore
    <ListView
      toggleOrder={toggleOrder}
      agencyData={creators}
      hasNextPage={hasNextPage}
      isInitialFetch={isInitialFetch}
      isFetchingNextPage={isFetchingNextPage}
      isFieldSorted={isFieldSorted}
      fetchNextPage={onFetchNextPage}
      isShared
      isLoading={isLoading}
    />
  );
}
